/**
 * Office-wide alert dismissals (TRIAGE-01) — one shared mechanism for every
 * ✕/✓-clear on the Today surfaces: persisted by stable key in
 * pegasus_alert_dismissal, visible to the whole office, recurring conditions
 * resurface under fresh keys. Used by TriageStream and the on-call panel's
 * "ran overnight" acknowledgments.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export function useDismissals() {
  return useQuery({
    queryKey: ["alert_dismissals"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("pegasus_alert_dismissal")
        .select("key")
        .limit(5000);
      if (error) throw error;
      return new Set(((data ?? []) as { key: string }[]).map((d) => d.key));
    },
  });
}

/** Batch form — "Clear all" sweeps every visible alert in one write. */
export function useDismissMany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (keys: string[]) => {
      if (keys.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const by = u?.user?.email ?? null;
      const { error } = await supabase
        .from("pegasus_alert_dismissal")
        .upsert(
          keys.map((key) => ({ key, dismissed_by: by })),
          { onConflict: "key", ignoreDuplicates: true },
        );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert_dismissals"] }),
  });
}

export function useDismiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_alert_dismissal")
        .insert({ key, dismissed_by: u?.user?.email ?? null });
      if (error && !error.message.includes("duplicate")) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alert_dismissals"] }),
  });
}
